// src/shared.jsx
// Shared-core primitives — regenerated from App.jsx canonical versions
// (Pass 2, Strategy B). App.jsx is the source of truth; this file replaces
// the prior stale pre-rebrand copies wholesale. Editorial 'Window Seat at
// Altitude' palette.

import { createContext, useContext, useState } from "react";

// ── Booking gate — earliest selectable date for ALL travel pickers ──
// While JGA is in pre-launch, no travel dates earlier than this should
// be bookable. The min attribute on every <input type="date"> in the
// app routes through getMinTravelDate() so this is the only line that
// needs to change when bookings actually open.
//
// Format: "YYYY-MM-DD" (HTML5 date input format)
// To remove the gate entirely once we're live: set this to a past date.
export const BOOKING_OPEN_DATE = "2026-07-01";

// Returns the YYYY-MM-DD string that should be used as the `min` on a
// date input. Picks the later of today vs the booking-open date — so
// after launch this naturally becomes today; before launch it locks
// the picker to July 1+.
export function getMinTravelDate() {
  const today = new Date().toISOString().slice(0, 10);
  return today > BOOKING_OPEN_DATE ? today : BOOKING_OPEN_DATE;
}

// Add N days to a YYYY-MM-DD string. Returns "" if input is empty.
export function addDaysISO(dateStr, days) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

// Days between two YYYY-MM-DD strings (0 if either is missing).
export function daysBetweenISO(a, b) {
  if (!a || !b) return 0;
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}

// Shared handler for all booking-date departure inputs. Wraps three behaviors:
//  1) Clamp past dates to getMinTravelDate() — defensive against typed/pasted values
//  2) If return is empty, auto-fill to depart + defaultDays (default 7 = week-long trip;
//     callers can override e.g. a saved 3-day itinerary passes 3)
//  3) If return is set, PRESERVE the original trip duration when shifting depart
//     (e.g. user had Jul 30 → Aug 6 = 7 days; changing depart to Aug 20 yields
//      Aug 27 — keeps the 7-day intent). Falls back to depart+1 if duration
//      collapsed to 0/negative somehow.
// Use this in onChange instead of inlining logic — keeps every booking date
// picker behaving identically.
export function applyDepartChange(v, currentDepart, currentReturn, setDepart, setReturn, defaultDays = 7) {
  if (!v) { setDepart(""); return; }
  const floor = getMinTravelDate();
  if (v < floor) v = floor;
  const originalGap = daysBetweenISO(currentDepart, currentReturn);
  setDepart(v);
  if (!currentReturn) {
    setReturn(addDaysISO(v, defaultDays));
  } else if (originalGap > 0) {
    setReturn(addDaysISO(v, originalGap));
  } else if (currentReturn <= v) {
    setReturn(addDaysISO(v, 1));
  }
}

// Shared handler for booking-date return inputs. Snap-forward (not silent
// reject) if user picks return on/before depart — picks depart+1 as the
// minimum sensible value. Caller can layer a max-cap on top (e.g. 21-day
// trip ceiling) by validating v before calling, since the caller knows the
// site-specific ceiling.
export function applyReturnChange(v, currentDepart, setReturn) {
  if (!v) { setReturn(""); return; }
  const floor = currentDepart || getMinTravelDate();
  if (v <= floor) v = addDaysISO(floor, 1);
  setReturn(v);
}

// Returns true if we're still in pre-launch (used for hint copy).
export function isPreLaunch() {
  return new Date().toISOString().slice(0, 10) < BOOKING_OPEN_DATE;
}

// ── Theme context — lets sub-components read darkMode ────────────
export const ThemeContext = createContext({
  darkMode: true,
  textPrimary: "#1B3A5C", textMuted: "#4A6B8A", textDim: "#94A3B8",
  borderColor: "#B8C9D4",
  surfaceBg: "#F0EDE3",
  cardBg: "#FAFAF5",
  goldText: "#1B3A5C", amberText: "#1B3A5C", creamText: "#1B3A5C",
  // Canonical editorial palette — what the rest of the app will read after sweep
  bgCream: "#FAFAF5", bgCreamDeep: "#F0EDE3", bgDarkAccent: "#15202b",
  inkDark: "#1B3A5C", inkMid: "#4A6B8A", inkBurgundy: "#1B3A5C",
  inkGold: "#4A8CC4", inkOnDark: "#FAFAF5", borderCream: "#B8C9D4",
  navBg: "#1B3A5C", navInk: "#FAFAF5", navAccent: "#4A8CC4",
  // Editorial aliases (backward compat with Profile + Surprise Me — will phase out post-sweep)
  editorialCream: "#FAFAF5", editorialCreamDeep: "#F0EDE3", editorialBorder: "#B8C9D4",
  editorialInkBurgundy: "#1B3A5C", editorialInkDark: "#1B3A5C", editorialInkMid: "#4A6B8A"
});

export const useTheme = () => useContext(ThemeContext);

// ── Destination data ─────────────────────────────────────────────
export const DESTINATIONS = [
  { name: "Lisbon", country: "Portugal", state: null, emoji: "🇵🇹", region: "Europe", subRegion: "Western Europe", minBudget: 600, maxBudget: 1800, tags: ["culture","food","beach","history","romance"], temp: "22°C", vibe: "Laid-back & charming" },
  { name: "Porto", country: "Portugal", state: null, emoji: "🇵🇹", region: "Europe", subRegion: "Western Europe", minBudget: 500, maxBudget: 1500, tags: ["culture","food","romance","history","art"], temp: "19°C", vibe: "Soulful & wine-soaked" },
  { name: "Paris", country: "France", state: null, emoji: "🇫🇷", region: "Europe", subRegion: "Western Europe", minBudget: 1000, maxBudget: 2500, tags: ["romance","culture","food","art","luxury","shopping"], temp: "17°C", vibe: "Iconic & effortlessly chic" },
  { name: "Barcelona", country: "Spain", state: "Catalonia", emoji: "🇪🇸", region: "Europe", subRegion: "Southern Europe", minBudget: 800, maxBudget: 2000, tags: ["beach","culture","food","art","nightlife","shopping"], temp: "22°C", vibe: "Vibrant & sun-kissed" },
  { name: "Rome", country: "Italy", state: "Lazio", emoji: "🇮🇹", region: "Europe", subRegion: "Southern Europe", minBudget: 900, maxBudget: 2200, tags: ["culture","food","romance","history","art"], temp: "21°C", vibe: "Eternal & indulgent" },
  { name: "Amalfi Coast", country: "Italy", state: "Campania", emoji: "🇮🇹", region: "Europe", subRegion: "Southern Europe", minBudget: 1100, maxBudget: 2600, tags: ["beach","romance","food","luxury","wellness"], temp: "25°C", vibe: "Cinematic & indulgent" },
  { name: "Santorini", country: "Greece", state: "South Aegean", emoji: "🇬🇷", region: "Europe", subRegion: "Southern Europe", minBudget: 1000, maxBudget: 2500, tags: ["beach","romance","luxury","wellness","culture"], temp: "24°C", vibe: "Dreamy & sun-drenched" },
  { name: "Athens", country: "Greece", state: "Attica", emoji: "🇬🇷", region: "Europe", subRegion: "Southern Europe", minBudget: 700, maxBudget: 1800, tags: ["culture","food","history","art","nightlife"], temp: "23°C", vibe: "Ancient & electric" },
  { name: "Prague", country: "Czech Republic", state: null, emoji: "🇨🇿", region: "Europe", subRegion: "Eastern Europe", minBudget: 600, maxBudget: 1600, tags: ["culture","food","romance","history","art","nightlife"], temp: "15°C", vibe: "Fairy-tale & lively" },
  { name: "Budapest", country: "Hungary", state: null, emoji: "🇭🇺", region: "Europe", subRegion: "Eastern Europe", minBudget: 600, maxBudget: 1500, tags: ["culture","wellness","food","nightlife","history","romance"], temp: "16°C", vibe: "Grand & thermal" },
  { name: "Reykjavik", country: "Iceland", state: null, emoji: "🇮🇸", region: "Europe", subRegion: "Northern Europe", minBudget: 1200, maxBudget: 2800, tags: ["adventure","wellness","nature","romance"], temp: "7°C", vibe: "Wild & otherworldly" },
  { name: "Copenhagen", country: "Denmark", state: null, emoji: "🇩🇰", region: "Europe", subRegion: "Northern Europe", minBudget: 1100, maxBudget: 2400, tags: ["culture","food","wellness","art","shopping"], temp: "13°C", vibe: "Hygge & design-forward" },
  { name: "Bangkok", country: "Thailand", state: null, emoji: "🇹🇭", region: "Asia", subRegion: "Southeast Asia", minBudget: 500, maxBudget: 1500, tags: ["food","culture","nightlife","shopping"], temp: "31°C", vibe: "Vibrant & exotic" },
  { name: "Chiang Mai", country: "Thailand", state: "Chiang Mai Province", emoji: "🇹🇭", region: "Asia", subRegion: "Southeast Asia", minBudget: 400, maxBudget: 1200, tags: ["culture","wellness","nature","adventure","food"], temp: "28°C", vibe: "Tranquil & mystical" },
  { name: "Bali", country: "Indonesia", state: "Bali", emoji: "🇮🇩", region: "Asia", subRegion: "Southeast Asia", minBudget: 700, maxBudget: 2000, tags: ["beach","wellness","adventure","romance","luxury","culture"], temp: "29°C", vibe: "Spiritual & lush" },
  { name: "Lombok", country: "Indonesia", state: "West Nusa Tenggara", emoji: "🇮🇩", region: "Asia", subRegion: "Southeast Asia", minBudget: 600, maxBudget: 1600, tags: ["beach","adventure","nature"], temp: "28°C", vibe: "Raw & untouched" },
  { name: "Hoi An", country: "Vietnam", state: "Quảng Nam", emoji: "🇻🇳", region: "Asia", subRegion: "Southeast Asia", minBudget: 400, maxBudget: 1100, tags: ["culture","food","romance","art","beach"], temp: "26°C", vibe: "Lantern-lit & timeless" },
  { name: "Tokyo", country: "Japan", state: "Tokyo Metropolis", emoji: "🇯🇵", region: "Asia", subRegion: "East Asia", minBudget: 1100, maxBudget: 2800, tags: ["food","culture","shopping","art","nightlife"], temp: "19°C", vibe: "Futuristic & precise" },
  { name: "Kyoto", country: "Japan", state: "Kyoto Prefecture", emoji: "🇯🇵", region: "Asia", subRegion: "East Asia", minBudget: 900, maxBudget: 2200, tags: ["culture","wellness","romance","history","art","food"], temp: "17°C", vibe: "Serene & timeless" },
  { name: "Seoul", country: "South Korea", state: null, emoji: "🇰🇷", region: "Asia", subRegion: "East Asia", minBudget: 900, maxBudget: 2100, tags: ["food","culture","shopping","nightlife","art"], temp: "17°C", vibe: "Dynamic & stylish" },
  { name: "Maldives", country: "Maldives", state: null, emoji: "🇲🇻", region: "Asia", subRegion: "South Asia", minBudget: 2000, maxBudget: 5000, tags: ["beach","romance","wellness","luxury"], temp: "30°C", vibe: "Paradise & isolation" },
  { name: "New York City", country: "USA", state: "New York", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 1200, maxBudget: 3000, tags: ["culture","food","shopping","art","nightlife","luxury"], temp: "18°C", vibe: "Electric & iconic", hotelNightlyOverride: 300 },
  { name: "Miami", country: "USA", state: "Florida", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 900, maxBudget: 2200, tags: ["beach","nightlife","food","luxury","culture"], temp: "27°C", vibe: "Sultry & glamorous", hotelNightlyOverride: 150 },
  { name: "New Orleans", country: "USA", state: "Louisiana", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 700, maxBudget: 1800, tags: ["food","culture","nightlife","history","art","romance"], temp: "23°C", vibe: "Soulful & festive", hotelNightlyOverride: 150 },
  { name: "San Francisco", country: "USA", state: "California", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 1100, maxBudget: 2600, tags: ["culture","food","nature","art"], temp: "16°C", vibe: "Creative & foggy", hotelNightlyOverride: 150 },
  { name: "Honolulu", country: "USA", state: "Hawaii", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 1300, maxBudget: 3000, tags: ["beach","wellness","nature","adventure","culture"], temp: "28°C", vibe: "Aloha & volcanic wonder" },
  { name: "Los Angeles", country: "USA", state: "California", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 1000, maxBudget: 2800, tags: ["beach","culture","food","nightlife","luxury","art"], temp: "24°C", vibe: "Sun-drenched & cinematic", hotelNightlyOverride: 200 },
  { name: "Las Vegas", country: "USA", state: "Nevada", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 800, maxBudget: 2500, tags: ["nightlife","luxury","food","shopping"], temp: "32°C", vibe: "Neon & non-stop", hotelNightlyOverride: 150 },
  { name: "Chicago", country: "USA", state: "Illinois", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 900, maxBudget: 2200, tags: ["culture","food","art","shopping","history"], temp: "14°C", vibe: "Windy & world-class", hotelNightlyOverride: 200 },
  { name: "Nashville", country: "USA", state: "Tennessee", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 700, maxBudget: 1800, tags: ["nightlife","culture","food","art","romance"], temp: "20°C", vibe: "Music-soaked & spirited", hotelNightlyOverride: 200 },
  { name: "Austin", country: "USA", state: "Texas", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 700, maxBudget: 1800, tags: ["food","nightlife","culture","adventure"], temp: "27°C", vibe: "Keep it weird & vibrant", hotelNightlyOverride: 200 },
  { name: "Houston", country: "USA", state: "Texas", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 700, maxBudget: 1700, tags: ["food","culture","shopping","art"], temp: "29°C", vibe: "Diverse & dynamic", hotelNightlyOverride: 150 },
  { name: "Dallas", country: "USA", state: "Texas", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 700, maxBudget: 1700, tags: ["culture","food","shopping","nightlife","luxury"], temp: "26°C", vibe: "Big & bold", hotelNightlyOverride: 150 },
  { name: "San Antonio", country: "USA", state: "Texas", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 600, maxBudget: 1500, tags: ["culture","history","food","romance"], temp: "27°C", vibe: "Historic & spirited", hotelNightlyOverride: 140 },
  { name: "Washington DC", country: "USA", state: "District of Columbia", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 900, maxBudget: 2200, tags: ["culture","history","food","art"], temp: "16°C", vibe: "Grand & historic", hotelNightlyOverride: 200 },
  { name: "Boston", country: "USA", state: "Massachusetts", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 900, maxBudget: 2200, tags: ["culture","history","food","art"], temp: "12°C", vibe: "Cobblestone & collegiate", hotelNightlyOverride: 300 },
  { name: "Seattle", country: "USA", state: "Washington", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 900, maxBudget: 2200, tags: ["nature","food","culture","art","adventure"], temp: "13°C", vibe: "Rain-soaked & innovative", hotelNightlyOverride: 200 },
  { name: "Denver", country: "USA", state: "Colorado", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 800, maxBudget: 2000, tags: ["nature","adventure","food"], temp: "15°C", vibe: "Mile-high & outdoorsy", hotelNightlyOverride: 200 },
  { name: "Portland", country: "USA", state: "Oregon", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 800, maxBudget: 1900, tags: ["food","culture","nature","art"], temp: "13°C", vibe: "Quirky & green", hotelNightlyOverride: 160 },
  { name: "Phoenix", country: "USA", state: "Arizona", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 700, maxBudget: 1800, tags: ["wellness","nature","luxury","adventure"], temp: "35°C", vibe: "Desert chic & sun-baked", hotelNightlyOverride: 140 },
  { name: "San Diego", country: "USA", state: "California", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 900, maxBudget: 2200, tags: ["beach","food","nature","wellness","adventure"], temp: "22°C", vibe: "Chill & sun-kissed", hotelNightlyOverride: 200 },
  { name: "Atlanta", country: "USA", state: "Georgia", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 700, maxBudget: 1800, tags: ["culture","food","nightlife","history","shopping"], temp: "22°C", vibe: "Southern & soulful", hotelNightlyOverride: 150 },
  { name: "New York — The Hamptons", country: "USA", state: "New York", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 1500, maxBudget: 4000, tags: ["beach","luxury","wellness","romance","art"], temp: "19°C", vibe: "Breezy & elite", hotelNightlyOverride: 600 },
  { name: "Napa Valley", country: "USA", state: "California", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 1200, maxBudget: 3500, tags: ["food","luxury","romance","wellness","nature"], temp: "20°C", vibe: "Golden vines & fine dining", hotelNightlyOverride: 450 },
  { name: "Charleston", country: "USA", state: "South Carolina", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 700, maxBudget: 1800, tags: ["culture","history","food","romance"], temp: "21°C", vibe: "Antebellum charm & warmth", hotelNightlyOverride: 200 },
  { name: "Savannah", country: "USA", state: "Georgia", emoji: "🇺🇸", region: "Americas", subRegion: "North America", minBudget: 600, maxBudget: 1600, tags: ["culture","history","romance","art"], temp: "22°C", vibe: "Mossy & hauntingly beautiful", hotelNightlyOverride: 200 },
  { name: "Vancouver", country: "Canada", state: "British Columbia", emoji: "🇨🇦", region: "Americas", subRegion: "North America", minBudget: 900, maxBudget: 2200, tags: ["nature","culture","food","adventure"], temp: "12°C", vibe: "Crisp & cosmopolitan", hotelNightlyOverride: 200 },
  { name: "Mexico City", country: "Mexico", state: "CDMX", emoji: "🇲🇽", region: "Americas", subRegion: "North America", minBudget: 600, maxBudget: 1600, tags: ["food","culture","art","history"], temp: "22°C", vibe: "Bold & soulful", safetyNote: { level: 2, note: "Most central districts safe. Use Uber or registered taxis (avoid hailing). Avoid Tepito & Iztapalapa neighborhoods." } },
  { name: "Tulum", country: "Mexico", state: "Quintana Roo", emoji: "🇲🇽", region: "Americas", subRegion: "North America", minBudget: 800, maxBudget: 2000, tags: ["beach","wellness","culture","luxury","romance"], temp: "28°C", vibe: "Mystical & boho-luxe", safetyNote: { level: 2, note: "Beach zone safe during day. Avoid late-night travel between zones; use hotel-arranged transport at night." } },
  { name: "Buenos Aires", country: "Argentina", state: "Buenos Aires Province", emoji: "🇦🇷", region: "Americas", subRegion: "South America", minBudget: 700, maxBudget: 1800, tags: ["culture","food","romance","nightlife","art","history"], temp: "20°C", vibe: "Passionate & elegant" },
  { name: "Patagonia", country: "Argentina", state: "Santa Cruz", emoji: "🏔️", region: "Americas", subRegion: "South America", minBudget: 1500, maxBudget: 3500, tags: ["adventure","nature","wellness","wildlife"], temp: "10°C", vibe: "Epic & untamed" },
  { name: "Cartagena", country: "Colombia", state: "Bolívar", emoji: "🇨🇴", region: "Americas", subRegion: "South America", minBudget: 600, maxBudget: 1500, tags: ["beach","culture","romance","history","food"], temp: "29°C", vibe: "Colonial & colorful", safetyNote: { level: 3, note: "Historic Center & Bocagrande are safe. Stay in tourist zones, avoid late-night solo walks, don't display valuables." } },
  { name: "Marrakech", country: "Morocco", state: null, emoji: "🇲🇦", region: "Africa", subRegion: "North Africa", minBudget: 500, maxBudget: 1400, tags: ["culture","food","shopping","art","history","wellness"], temp: "28°C", vibe: "Colorful & mystical", safetyNote: { level: 2, note: "Medina safe with normal precautions. Hire licensed guides to avoid scams; beware of distraction theft in souks." } },
  { name: "Cairo", country: "Egypt", state: null, emoji: "🇪🇬", region: "Africa", subRegion: "North Africa", minBudget: 600, maxBudget: 1500, tags: ["culture","history","art"], temp: "30°C", vibe: "Ancient & overwhelming", safetyNote: { level: 2, note: "Cairo & Nile corridor are safe. Avoid Sinai & Western Desert. Stay alert near religious & government sites." } },
  { name: "Cape Town", country: "South Africa", state: "Western Cape", emoji: "🇿🇦", region: "Africa", subRegion: "Southern Africa", minBudget: 900, maxBudget: 2000, tags: ["beach","adventure","culture","nature","wildlife","food"], temp: "21°C", vibe: "Breathtaking & raw", safetyNote: { level: 2, note: "Tourist zones safe. Use registered transport, don't walk after dark, secure valuables — opportunistic crime can occur." } },
  { name: "Nairobi", country: "Kenya", state: "Nairobi County", emoji: "🇰🇪", region: "Africa", subRegion: "East Africa", minBudget: 1200, maxBudget: 3000, tags: ["adventure","nature","culture","wildlife"], temp: "23°C", vibe: "Wild & soulful", safetyNote: { level: 2, note: "Safari circuits & tourist zones safe. Avoid Eastleigh & Kibera neighborhoods; don't walk after dark; use registered transport." } },
  { name: "Dubai", country: "UAE", state: "Dubai Emirate", emoji: "🇦🇪", region: "Middle East", subRegion: "Gulf States", minBudget: 1300, maxBudget: 3500, tags: ["luxury","beach","culture","shopping","food","nightlife"], temp: "33°C", vibe: "Opulent & dazzling" },
  { name: "Abu Dhabi", country: "UAE", state: "Abu Dhabi Emirate", emoji: "🇦🇪", region: "Middle East", subRegion: "Gulf States", minBudget: 1200, maxBudget: 3000, tags: ["luxury","culture","wellness","art","history"], temp: "32°C", vibe: "Grand & understated" },
  { name: "Amman", country: "Jordan", state: null, emoji: "🇯🇴", region: "Middle East", subRegion: "Levant", minBudget: 700, maxBudget: 1800, tags: ["culture","history","adventure","food"], temp: "20°C", vibe: "Ancient & welcoming", safetyNote: { level: 2, note: "Amman, Petra, & Wadi Rum are safe. Avoid Syrian & Iraqi border areas (Level 4 zones)." } },
  // ── Oceania ──
  { name: "Sydney", country: "Australia", state: "New South Wales", emoji: "🇦🇺", region: "Oceania", subRegion: "Oceania", minBudget: 1400, maxBudget: 3200, tags: ["beach","culture","food","nightlife","art","shopping"], temp: "22°C", vibe: "Sun-soaked & cosmopolitan" },
  { name: "Melbourne", country: "Australia", state: "Victoria", emoji: "🇦🇺", region: "Oceania", subRegion: "Oceania", minBudget: 1300, maxBudget: 3000, tags: ["food","culture","art","nightlife","shopping"], temp: "17°C", vibe: "Cultured & caffeine-fuelled" },
  { name: "Cairns", country: "Australia", state: "Queensland", emoji: "🇦🇺", region: "Oceania", subRegion: "Oceania", minBudget: 1200, maxBudget: 2800, tags: ["adventure","nature","beach","wildlife"], temp: "27°C", vibe: "Tropical & wild" },
  { name: "Perth", country: "Australia", state: "Western Australia", emoji: "🇦🇺", region: "Oceania", subRegion: "Oceania", minBudget: 1200, maxBudget: 2800, tags: ["beach","nature","wellness","adventure"], temp: "24°C", vibe: "Laid-back & luminous" },
  { name: "Hobart", country: "Australia", state: "Tasmania", emoji: "🇦🇺", region: "Oceania", subRegion: "Oceania", minBudget: 1000, maxBudget: 2400, tags: ["culture","nature","food","adventure","art"], temp: "13°C", vibe: "Raw & quietly brilliant" },
  { name: "Auckland", country: "New Zealand", state: "Auckland", emoji: "🇳🇿", region: "Oceania", subRegion: "Oceania", minBudget: 1300, maxBudget: 3000, tags: ["adventure","nature","culture","food"], temp: "16°C", vibe: "Edge-of-the-world energy" },
  { name: "Queenstown", country: "New Zealand", state: "Otago", emoji: "🇳🇿", region: "Oceania", subRegion: "Oceania", minBudget: 1400, maxBudget: 3200, tags: ["adventure","nature","wellness","luxury","food"], temp: "12°C", vibe: "Adrenaline & alpine wonder" },
  { name: "Rotorua", country: "New Zealand", state: "Bay of Plenty", emoji: "🇳🇿", region: "Oceania", subRegion: "Oceania", minBudget: 1000, maxBudget: 2400, tags: ["culture","adventure","wellness","nature","history"], temp: "14°C", vibe: "Geothermal & Māori soul" },
  // ── Indonesia expanded ──
  { name: "Yogyakarta", country: "Indonesia", state: "Yogyakarta", emoji: "🇮🇩", region: "Asia", subRegion: "Southeast Asia", minBudget: 500, maxBudget: 1400, tags: ["culture","adventure","history","art","food"], temp: "28°C", vibe: "Ancient & artistic" },
  { name: "Komodo", country: "Indonesia", state: "Komodo", emoji: "🇮🇩", region: "Asia", subRegion: "Southeast Asia", minBudget: 800, maxBudget: 2200, tags: ["adventure","nature","wildlife","beach"], temp: "30°C", vibe: "Prehistoric & untamed" },
  { name: "Raja Ampat", country: "Indonesia", state: "Raja Ampat", emoji: "🇮🇩", region: "Asia", subRegion: "Southeast Asia", minBudget: 1500, maxBudget: 4000, tags: ["beach","adventure","nature","wildlife"], temp: "29°C", vibe: "Remote & breathtaking" },
];

// ── Destination coordinates for distance-based flight pricing ────
// Used by estimateFlightPrice when no live API is available
export const DEST_COORDS = {
  "Paris": { lat: 48.8566, lng: 2.3522 },
  "Rome": { lat: 41.9028, lng: 12.4964 },
  "Barcelona": { lat: 41.3851, lng: 2.1734 },
  "Lisbon": { lat: 38.7223, lng: -9.1393 },
  "Tokyo": { lat: 35.6762, lng: 139.6503 },
  "Kyoto": { lat: 35.0116, lng: 135.7681 },
  "Bangkok": { lat: 13.7563, lng: 100.5018 },
  "Bali": { lat: -8.3405, lng: 115.0920 },
  "Sydney": { lat: -33.8688, lng: 151.2093 },
  "Dubai": { lat: 25.2048, lng: 55.2708 },
  "New York City": { lat: 40.7128, lng: -74.0060 },
  "Miami": { lat: 25.7617, lng: -80.1918 },
  "Honolulu": { lat: 21.3099, lng: -157.8581 },
  "London": { lat: 51.5074, lng: -0.1278 },
  "Amsterdam": { lat: 52.3676, lng: 4.9041 },
  "Athens": { lat: 37.9838, lng: 23.7275 },
  "Prague": { lat: 50.0755, lng: 14.4378 },
  "Budapest": { lat: 47.4979, lng: 19.0402 },
  "Santorini": { lat: 36.3932, lng: 25.4615 },
  "Seoul": { lat: 37.5665, lng: 126.9780 },
  "Marrakech": { lat: 31.6295, lng: -7.9811 },
  "Cape Town": { lat: -33.9249, lng: 18.4241 },
  "Mexico City": { lat: 19.4326, lng: -99.1332 },
  "Tulum": { lat: 20.2114, lng: -87.4654 },
  "Cartagena": { lat: 10.3910, lng: -75.4794 },
  "Buenos Aires": { lat: -34.6037, lng: -58.3816 },
  "Reykjavik": { lat: 64.1466, lng: -21.9426 },
  "Copenhagen": { lat: 55.6761, lng: 12.5683 },
  "Chiang Mai": { lat: 18.7883, lng: 98.9853 },
  "Hoi An": { lat: 15.8801, lng: 108.3380 },
  "Nairobi": { lat: -1.2921, lng: 36.8219 },
  "Cairo": { lat: 30.0444, lng: 31.2357 },
  // ── Bulk add: 41 destinations previously missing from DEST_COORDS ──
  // Without these, estimateFlightPrice returned null → MA/TT/Explore all fell
  // back to subregion regional defaults ($750 generic, $1500 Oceania, etc).
  // Real per-route distance pricing now works for all 71 catalog destinations.
  "Atlanta": { lat: 33.7490, lng: -84.3880 },
  "Austin": { lat: 30.2670, lng: -97.7430 },
  "Boston": { lat: 42.3610, lng: -71.0570 },
  "Charleston": { lat: 32.7760, lng: -79.9310 },
  "Chicago": { lat: 41.8780, lng: -87.6300 },
  "Dallas": { lat: 32.7760, lng: -96.7970 },
  "Denver": { lat: 39.7390, lng: -104.9900 },
  "Houston": { lat: 29.7600, lng: -95.3700 },
  "Las Vegas": { lat: 36.1690, lng: -115.1390 },
  "Los Angeles": { lat: 34.0520, lng: -118.2440 },
  "Napa Valley": { lat: 38.2970, lng: -122.2860 },
  "Nashville": { lat: 36.1620, lng: -86.7810 },
  "New Orleans": { lat: 29.9510, lng: -90.0710 },
  "New York — The Hamptons": { lat: 40.9640, lng: -72.1840 },
  "Phoenix": { lat: 33.4480, lng: -112.0740 },
  "Portland": { lat: 45.5150, lng: -122.6790 },
  "San Antonio": { lat: 29.4240, lng: -98.4930 },
  "San Diego": { lat: 32.7150, lng: -117.1610 },
  "San Francisco": { lat: 37.7740, lng: -122.4190 },
  "Savannah": { lat: 32.0800, lng: -81.0920 },
  "Seattle": { lat: 47.6060, lng: -122.3320 },
  "Washington DC": { lat: 38.9070, lng: -77.0370 },
  "Abu Dhabi": { lat: 24.4660, lng: 54.3660 },
  "Accra": { lat: 5.6030, lng: -0.1870 },
  "Amalfi Coast": { lat: 40.6330, lng: 14.6020 },
  "Amman": { lat: 31.9450, lng: 35.9280 },
  "Auckland": { lat: -36.8480, lng: 174.7620 },
  "Cairns": { lat: -16.9180, lng: 145.7780 },
  "Hobart": { lat: -42.8800, lng: 147.3250 },
  "Komodo": { lat: -8.5550, lng: 119.4760 },
  "Lombok": { lat: -8.6500, lng: 116.3240 },
  "Maldives": { lat: 4.1750, lng: 73.5090 },
  "Melbourne": { lat: -37.8140, lng: 144.9630 },
  "Patagonia": { lat: -50.3390, lng: -72.2650 },
  "Perth": { lat: -31.9530, lng: 115.8570 },
  "Porto": { lat: 41.1580, lng: -8.6290 },
  "Queenstown": { lat: -45.0310, lng: 168.6630 },
  "Raja Ampat": { lat: -0.2340, lng: 130.5270 },
  "Rotorua": { lat: -38.1370, lng: 176.2490 },
  "Vancouver": { lat: 49.2820, lng: -123.1210 },
  "Yogyakarta": { lat: -7.7970, lng: 110.3700 },
};

// Limited-service domestic destinations where round-trip fares run ~1.5-2x
// competitive markets due to supply constraints (few direct routes, no ULCC
// pressure, often require ATL connection). Calibrated against Cas's audit
// where CHS and SAV came in at $600+ vs the formula's $200 floor.
export const LIMITED_SERVICE_DOMESTIC = new Set([
  "Charleston",   // CHS — limited direct service from most hubs
  "Savannah",     // SAV — limited direct service from most hubs
]);

// ── Major airports database (lat, lng, code, name, city) ──────────
export const MAJOR_AIRPORTS = [
  // USA
  { code:"JFK", name:"John F. Kennedy", city:"New York", lat:40.6413, lng:-73.7781 },
  { code:"LGA", name:"LaGuardia", city:"New York", lat:40.7772, lng:-73.8726 },
  { code:"EWR", name:"Newark Liberty", city:"Newark", lat:40.6895, lng:-74.1745 },
  { code:"LAX", name:"Los Angeles Intl", city:"Los Angeles", lat:33.9425, lng:-118.4081 },
  { code:"ORD", name:"O'Hare Intl", city:"Chicago", lat:41.9742, lng:-87.9073 },
  { code:"MDW", name:"Midway", city:"Chicago", lat:41.7868, lng:-87.7522 },
  { code:"ATL", name:"Hartsfield-Jackson", city:"Atlanta", lat:33.6407, lng:-84.4277 },
  { code:"DFW", name:"Dallas/Fort Worth", city:"Dallas", lat:32.8998, lng:-97.0403 },
  { code:"DAL", name:"Love Field", city:"Dallas", lat:32.8471, lng:-96.8518 },
  { code:"DEN", name:"Denver Intl", city:"Denver", lat:39.8561, lng:-104.6737 },
  { code:"SFO", name:"San Francisco Intl", city:"San Francisco", lat:37.6213, lng:-122.379 },
  { code:"OAK", name:"Oakland Intl", city:"Oakland", lat:37.7213, lng:-122.2208 },
  { code:"SJC", name:"San Jose Intl", city:"San Jose", lat:37.3626, lng:-121.9290 },
  { code:"SEA", name:"Seattle-Tacoma", city:"Seattle", lat:47.4502, lng:-122.3088 },
  { code:"MIA", name:"Miami Intl", city:"Miami", lat:25.7959, lng:-80.2870 },
  { code:"FLL", name:"Fort Lauderdale", city:"Fort Lauderdale", lat:26.0726, lng:-80.1527 },
  { code:"MCO", name:"Orlando Intl", city:"Orlando", lat:28.4294, lng:-81.3089 },
  { code:"TPA", name:"Tampa Intl", city:"Tampa", lat:27.9755, lng:-82.5332 },
  { code:"BOS", name:"Logan Intl", city:"Boston", lat:42.3656, lng:-71.0096 },
  { code:"PHL", name:"Philadelphia Intl", city:"Philadelphia", lat:39.8729, lng:-75.2437 },
  { code:"DCA", name:"Reagan National", city:"Washington DC", lat:38.8521, lng:-77.0377 },
  { code:"IAD", name:"Dulles Intl", city:"Washington DC", lat:38.9531, lng:-77.4565 },
  { code:"BWI", name:"Baltimore/Washington", city:"Baltimore", lat:39.1754, lng:-76.6683 },
  { code:"MSP", name:"Minneapolis-Saint Paul", city:"Minneapolis", lat:44.8848, lng:-93.2223 },
  { code:"DTW", name:"Detroit Metro", city:"Detroit", lat:42.2162, lng:-83.3554 },
  { code:"PHX", name:"Phoenix Sky Harbor", city:"Phoenix", lat:33.4373, lng:-112.0078 },
  { code:"LAS", name:"Harry Reid Intl", city:"Las Vegas", lat:36.0840, lng:-115.1537 },
  { code:"CLT", name:"Charlotte Douglas", city:"Charlotte", lat:35.2144, lng:-80.9473 },
  { code:"IAH", name:"George Bush Intl", city:"Houston", lat:29.9902, lng:-95.3368 },
  { code:"HOU", name:"Hobby Airport", city:"Houston", lat:29.6454, lng:-95.2789 },
  { code:"SAT", name:"San Antonio Intl", city:"San Antonio", lat:29.5337, lng:-98.4698 },
  { code:"AUS", name:"Austin-Bergstrom", city:"Austin", lat:30.1975, lng:-97.6664 },
  { code:"SLC", name:"Salt Lake City Intl", city:"Salt Lake City", lat:40.7884, lng:-111.9778 },
  { code:"PDX", name:"Portland Intl", city:"Portland", lat:45.5898, lng:-122.5951 },
  { code:"SAN", name:"San Diego Intl", city:"San Diego", lat:32.7338, lng:-117.1933 },
  { code:"MSY", name:"Louis Armstrong", city:"New Orleans", lat:29.9934, lng:-90.2580 },
  { code:"STL", name:"St. Louis Lambert", city:"St. Louis", lat:38.7487, lng:-90.3700 },
  { code:"MCI", name:"Kansas City Intl", city:"Kansas City", lat:39.2976, lng:-94.7139 },
  { code:"RDU", name:"Raleigh-Durham", city:"Raleigh", lat:35.8776, lng:-78.7875 },
  { code:"BNA", name:"Nashville Intl", city:"Nashville", lat:36.1245, lng:-86.6782 },
  { code:"MEM", name:"Memphis Intl", city:"Memphis", lat:35.0424, lng:-89.9767 },
  { code:"CVG", name:"Cincinnati/N. Kentucky", city:"Cincinnati", lat:39.0488, lng:-84.6678 },
  { code:"IND", name:"Indianapolis Intl", city:"Indianapolis", lat:39.7173, lng:-86.2944 },
  { code:"CMH", name:"John Glenn Columbus", city:"Columbus", lat:39.9980, lng:-82.8919 },
  { code:"CLE", name:"Cleveland Hopkins", city:"Cleveland", lat:41.4117, lng:-81.8498 },
  { code:"PIT", name:"Pittsburgh Intl", city:"Pittsburgh", lat:40.4915, lng:-80.2329 },
  { code:"BUF", name:"Buffalo Niagara", city:"Buffalo", lat:42.9405, lng:-78.7322 },
  { code:"ALB", name:"Albany Intl", city:"Albany", lat:42.7483, lng:-73.8017 },
  { code:"OKC", name:"Will Rogers World", city:"Oklahoma City", lat:35.3931, lng:-97.6007 },
  { code:"TUL", name:"Tulsa Intl", city:"Tulsa", lat:36.1984, lng:-95.8881 },
  { code:"ABQ", name:"Albuquerque Intl", city:"Albuquerque", lat:35.0402, lng:-106.6090 },
  { code:"ELP", name:"El Paso Intl", city:"El Paso", lat:31.8072, lng:-106.3779 },
  { code:"TUS", name:"Tucson Intl", city:"Tucson", lat:32.1161, lng:-110.9410 },
  { code:"BOI", name:"Boise Airport", city:"Boise", lat:43.5644, lng:-116.2228 },
  { code:"BZN", name:"Bozeman Yellowstone", city:"Bozeman", lat:45.7777, lng:-111.1528 },
  { code:"GEG", name:"Spokane Intl", city:"Spokane", lat:47.6199, lng:-117.5338 },
  { code:"FAI", name:"Fairbanks Intl", city:"Fairbanks", lat:64.8151, lng:-147.8560 },
  { code:"ANC", name:"Ted Stevens Anchorage", city:"Anchorage", lat:61.1744, lng:-149.9961 },
  { code:"HNL", name:"Daniel K. Inouye", city:"Honolulu", lat:21.3245, lng:-157.9251 },
  { code:"OGG", name:"Kahului Airport", city:"Maui", lat:20.8986, lng:-156.4305 },
  { code:"SJU", name:"Luis Muñoz Marín", city:"San Juan", lat:18.4394, lng:-66.0018 },
  { code:"RSW", name:"Southwest Florida", city:"Fort Myers", lat:26.5362, lng:-81.7552 },
  { code:"PBI", name:"Palm Beach Intl", city:"West Palm Beach", lat:26.6832, lng:-80.0956 },
  { code:"JAX", name:"Jacksonville Intl", city:"Jacksonville", lat:30.4941, lng:-81.6879 },
  { code:"SAV", name:"Savannah/Hilton Head", city:"Savannah", lat:32.1276, lng:-81.2021 },
  { code:"CHS", name:"Charleston Intl", city:"Charleston", lat:32.8987, lng:-80.0405 },
  { code:"MYR", name:"Myrtle Beach Intl", city:"Myrtle Beach", lat:33.6797, lng:-78.9283 },
  { code:"GSP", name:"Greenville-Spartanburg", city:"Greenville", lat:34.8957, lng:-82.2189 },
  { code:"AVL", name:"Asheville Regional", city:"Asheville", lat:35.4362, lng:-82.5418 },
  { code:"ORF", name:"Norfolk Intl", city:"Norfolk", lat:36.8976, lng:-76.0126 },
  { code:"RIC", name:"Richmond Intl", city:"Richmond", lat:37.5052, lng:-77.3197 },
  { code:"SDF", name:"Louisville Intl", city:"Louisville", lat:38.1744, lng:-85.7360 },
  { code:"LEX", name:"Blue Grass Airport", city:"Lexington", lat:38.0365, lng:-84.6059 },
  { code:"BHM", name:"Birmingham-Shuttlesworth", city:"Birmingham", lat:33.5629, lng:-86.7535 },
  { code:"HSV", name:"Huntsville Intl", city:"Huntsville", lat:34.6372, lng:-86.7751 },
  { code:"MOB", name:"Mobile Regional", city:"Mobile", lat:30.6913, lng:-88.2428 },
  { code:"JAN", name:"Jackson-Medgar Wiley Evers", city:"Jackson", lat:32.3112, lng:-90.0759 },
  { code:"LIT", name:"Clinton National", city:"Little Rock", lat:34.7294, lng:-92.2243 },
  { code:"XNA", name:"Northwest Arkansas", city:"Fayetteville", lat:36.2819, lng:-94.3069 },
  { code:"DSM", name:"Des Moines Intl", city:"Des Moines", lat:41.5340, lng:-93.6631 },
  { code:"CID", name:"Eastern Iowa Airport", city:"Cedar Rapids", lat:41.8847, lng:-91.7108 },
  { code:"MSN", name:"Dane County Regional", city:"Madison", lat:43.1399, lng:-89.3375 },
  { code:"MKE", name:"Milwaukee Mitchell", city:"Milwaukee", lat:42.9472, lng:-87.8966 },
  { code:"GRB", name:"Green Bay Austin Straubel", city:"Green Bay", lat:44.4851, lng:-88.1296 },
  { code:"GRR", name:"Gerald R. Ford Intl", city:"Grand Rapids", lat:42.8808, lng:-85.5228 },
  { code:"FNT", name:"Bishop International", city:"Flint", lat:42.9655, lng:-83.7436 },
  { code:"LAN", name:"Capital Region Intl", city:"Lansing", lat:42.7787, lng:-84.5874 },
  { code:"FAR", name:"Hector Intl", city:"Fargo", lat:46.9207, lng:-96.8158 },
  { code:"BIS", name:"Bismarck Municipal", city:"Bismarck", lat:46.7727, lng:-100.7467 },
  { code:"FSD", name:"Sioux Falls Regional", city:"Sioux Falls", lat:43.5820, lng:-96.7419 },
  { code:"RAP", name:"Rapid City Regional", city:"Rapid City", lat:44.0453, lng:-103.0574 },
  { code:"COS", name:"Colorado Springs Airport", city:"Colorado Springs", lat:38.8059, lng:-104.7008 },
  { code:"GJT", name:"Grand Junction Regional", city:"Grand Junction", lat:39.1224, lng:-108.5267 },
  { code:"ASE", name:"Aspen/Pitkin County", city:"Aspen", lat:39.2232, lng:-106.8689 },
  { code:"JAC", name:"Jackson Hole Airport", city:"Jackson Hole", lat:43.6073, lng:-110.7378 },
  { code:"CPR", name:"Casper/Natrona County", city:"Casper", lat:42.9080, lng:-106.4644 },
  { code:"CHY", name:"Cheyenne Regional", city:"Cheyenne", lat:41.1557, lng:-104.8119 },
  { code:"RNO", name:"Reno-Tahoe Intl", city:"Reno", lat:39.4991, lng:-119.7681 },
  { code:"HND", name:"Henderson Executive", city:"Henderson", lat:35.9728, lng:-115.1344 },
  { code:"SMF", name:"Sacramento Intl", city:"Sacramento", lat:38.6954, lng:-121.5908 },
  { code:"SNA", name:"John Wayne Airport", city:"Orange County", lat:33.6757, lng:-117.8682 },
  { code:"BUR", name:"Hollywood Burbank", city:"Burbank", lat:34.2007, lng:-118.3585 },
  { code:"LGB", name:"Long Beach Airport", city:"Long Beach", lat:33.8177, lng:-118.1516 },
  { code:"ONT", name:"Ontario Intl", city:"Ontario", lat:34.0560, lng:-117.6012 },
  { code:"PSP", name:"Palm Springs Intl", city:"Palm Springs", lat:33.8297, lng:-116.5067 },
  { code:"SBA", name:"Santa Barbara Municipal", city:"Santa Barbara", lat:34.4262, lng:-119.8404 },
  { code:"SBP", name:"San Luis Obispo County", city:"San Luis Obispo", lat:35.2368, lng:-120.6424 },
  { code:"MRY", name:"Monterey Regional", city:"Monterey", lat:36.5870, lng:-121.8428 },
  { code:"FAT", name:"Fresno Yosemite Intl", city:"Fresno", lat:36.7762, lng:-119.7182 },
  { code:"BFL", name:"Meadows Field", city:"Bakersfield", lat:35.4336, lng:-119.0568 },
  { code:"RDD", name:"Redding Municipal", city:"Redding", lat:40.5090, lng:-122.2933 },
  { code:"MFR", name:"Rogue Valley Intl", city:"Medford", lat:42.3742, lng:-122.8735 },
  { code:"EUG", name:"Eugene Airport", city:"Eugene", lat:44.1246, lng:-123.2119 },
  { code:"RDM", name:"Roberts Field", city:"Bend", lat:44.2541, lng:-121.1500 },
  { code:"YKM", name:"Yakima Air Terminal", city:"Yakima", lat:46.5682, lng:-120.5440 },
  { code:"BLI", name:"Bellingham Intl", city:"Bellingham", lat:48.7928, lng:-122.5375 },
  { code:"GEG", name:"Spokane Intl", city:"Spokane", lat:47.6199, lng:-117.5338 },
  // Canada
  { code:"YYZ", name:"Toronto Pearson", city:"Toronto", lat:43.6777, lng:-79.6248 },
  { code:"YVR", name:"Vancouver Intl", city:"Vancouver", lat:49.1967, lng:-123.1815 },
  { code:"YUL", name:"Montréal-Trudeau", city:"Montreal", lat:45.4706, lng:-73.7408 },
  { code:"YYC", name:"Calgary Intl", city:"Calgary", lat:51.1315, lng:-114.0106 },
  { code:"YEG", name:"Edmonton Intl", city:"Edmonton", lat:53.3097, lng:-113.5827 },
  { code:"YOW", name:"Ottawa Macdonald-Cartier", city:"Ottawa", lat:45.3225, lng:-75.6692 },
  // UK & Europe
  { code:"LHR", name:"Heathrow", city:"London", lat:51.4700, lng:-0.4543 },
  { code:"LGW", name:"Gatwick", city:"London", lat:51.1481, lng:-0.1903 },
  { code:"CDG", name:"Charles de Gaulle", city:"Paris", lat:49.0097, lng:2.5479 },
  { code:"AMS", name:"Schiphol", city:"Amsterdam", lat:52.3086, lng:4.7639 },
  { code:"FRA", name:"Frankfurt Airport", city:"Frankfurt", lat:50.0379, lng:8.5622 },
  { code:"MAD", name:"Adolfo Suárez", city:"Madrid", lat:40.4839, lng:-3.5680 },
  { code:"BCN", name:"El Prat", city:"Barcelona", lat:41.2974, lng:2.0833 },
  { code:"FCO", name:"Fiumicino", city:"Rome", lat:41.8003, lng:12.2389 },
  { code:"MXP", name:"Malpensa", city:"Milan", lat:45.6306, lng:8.7281 },
  { code:"ATH", name:"Eleftherios Venizelos", city:"Athens", lat:37.9364, lng:23.9445 },
  { code:"LIS", name:"Humberto Delgado", city:"Lisbon", lat:38.7742, lng:-9.1342 },
  { code:"ZRH", name:"Zurich Airport", city:"Zurich", lat:47.4647, lng:8.5492 },
  { code:"CPH", name:"Copenhagen Airport", city:"Copenhagen", lat:55.6180, lng:12.6508 },
  { code:"ARN", name:"Stockholm Arlanda", city:"Stockholm", lat:59.6519, lng:17.9186 },
  { code:"OSL", name:"Oslo Gardermoen", city:"Oslo", lat:60.1976, lng:11.1004 },
  { code:"HEL", name:"Helsinki Airport", city:"Helsinki", lat:60.3183, lng:24.9630 },
  // Asia Pacific
  { code:"NRT", name:"Narita Intl", city:"Tokyo", lat:35.7720, lng:140.3929 },
  { code:"HND", name:"Haneda Airport", city:"Tokyo", lat:35.5494, lng:139.7798 },
  { code:"ICN", name:"Incheon Intl", city:"Seoul", lat:37.4602, lng:126.4407 },
  { code:"PEK", name:"Beijing Capital", city:"Beijing", lat:40.0799, lng:116.6031 },
  { code:"PVG", name:"Shanghai Pudong", city:"Shanghai", lat:31.1443, lng:121.8083 },
  { code:"HKG", name:"Hong Kong Intl", city:"Hong Kong", lat:22.3080, lng:113.9185 },
  { code:"SIN", name:"Singapore Changi", city:"Singapore", lat:1.3644, lng:103.9915 },
  { code:"BKK", name:"Suvarnabhumi", city:"Bangkok", lat:13.6900, lng:100.7501 },
  { code:"KUL", name:"Kuala Lumpur Intl", city:"Kuala Lumpur", lat:2.7456, lng:101.7099 },
  { code:"CGK", name:"Soekarno-Hatta", city:"Jakarta", lat:-6.1256, lng:106.6559 },
  { code:"DPS", name:"Ngurah Rai Intl", city:"Bali", lat:-8.7482, lng:115.1672 },
  { code:"MNL", name:"Ninoy Aquino Intl", city:"Manila", lat:14.5086, lng:121.0197 },
  { code:"SYD", name:"Sydney Airport", city:"Sydney", lat:-33.9399, lng:151.1753 },
  { code:"MEL", name:"Melbourne Airport", city:"Melbourne", lat:-37.6690, lng:144.8410 },
  { code:"BNE", name:"Brisbane Airport", city:"Brisbane", lat:-27.3842, lng:153.1175 },
  { code:"AKL", name:"Auckland Airport", city:"Auckland", lat:-37.0082, lng:174.7850 },
  { code:"DEL", name:"Indira Gandhi Intl", city:"Delhi", lat:28.5562, lng:77.1000 },
  { code:"BOM", name:"Chhatrapati Shivaji", city:"Mumbai", lat:19.0896, lng:72.8656 },
  // Middle East & Africa
  { code:"DXB", name:"Dubai Intl", city:"Dubai", lat:25.2532, lng:55.3657 },
  { code:"DOH", name:"Hamad Intl", city:"Doha", lat:25.2731, lng:51.6080 },
  { code:"CAI", name:"Cairo Intl", city:"Cairo", lat:30.1219, lng:31.4056 },
  { code:"JNB", name:"O.R. Tambo Intl", city:"Johannesburg", lat:-26.1367, lng:28.2411 },
  { code:"CPT", name:"Cape Town Intl", city:"Cape Town", lat:-33.9648, lng:18.6017 },
  // Latin America
  { code:"GRU", name:"São Paulo/Guarulhos", city:"São Paulo", lat:-23.4356, lng:-46.4731 },
  { code:"GIG", name:"Rio de Janeiro/Galeão", city:"Rio de Janeiro", lat:-22.8099, lng:-43.2506 },
  { code:"BOG", name:"El Dorado Intl", city:"Bogotá", lat:4.7016, lng:-74.1469 },
  { code:"LIM", name:"Jorge Chávez Intl", city:"Lima", lat:-12.0219, lng:-77.1143 },
  { code:"SCL", name:"Arturo Merino Benítez", city:"Santiago", lat:-33.3930, lng:-70.7858 },
  { code:"MEX", name:"Benito Juárez Intl", city:"Mexico City", lat:19.4363, lng:-99.0721 },
  { code:"CUN", name:"Cancún Intl", city:"Cancún", lat:21.0365, lng:-86.8770 },
  { code:"CTG", name:"Rafael Núñez Intl", city:"Cartagena", lat:10.4424, lng:-75.5130 },
];

// Haversine distance in miles
export function distanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3959; // Earth radius miles
  const toRad = x => x * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Distance-based round-trip flight estimate (per person, economy)
// Returns null if data missing. Uses tiered per-mile pricing + seasonal multiplier.
// Booking-window multiplier for domestic flights. Last-minute domestic
// fares spike dramatically — 4-day-out economy is typically 1.5-2x the
// 30+ day advance price. Calibrated against CLT route audit (Jun 2026).
// Does NOT apply to international (separate seasonal curve handles that).
// `miles` parameter dampens the spike for long-haul routes where ULCC
// competition and route inventory blunt the last-minute markup.
export function getDomesticBookingWindowMultiplier(dateStr, miles = null) {
  if (!dateStr) return 1.0; // No date = baseline (no premium applied)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const departure = new Date(dateStr);
  if (isNaN(departure.getTime())) return 1.0;
  const daysOut = Math.floor((departure - today) / (24 * 60 * 60 * 1000));
  if (daysOut < 0) return 1.0;        // Past date — defensive
  let mult;
  if (daysOut <= 3) mult = 2.4;       // Panic-buy window
  else if (daysOut <= 7) mult = 1.8;  // Last-minute (Cas's audit hit this band)
  else if (daysOut <= 14) mult = 1.35;// Short-notice
  else if (daysOut <= 30) mult = 1.08;// Standard advance
  else return 1.0;                     // Advance booking — baseline
  // Long-haul damping: transcontinental routes (>1500mi) have more inventory
  // and heavier ULCC competition (Southwest/Frontier/Spirit), so the
  // last-minute spike is roughly half as severe as on regional hops.
  if (miles !== null && miles > 1500) {
    mult = 1.0 + (mult - 1.0) * 0.5;
  }
  return mult;
}

export function estimateFlightPrice(originCode, destName, dateStr) {
  if (!originCode || !destName) return null;
  // Lookup origin from MAJOR_AIRPORTS (defined later in file but available at runtime)
  const origin = (typeof MAJOR_AIRPORTS !== "undefined") ? MAJOR_AIRPORTS.find(a => a.code === originCode) : null;
  const dest = DEST_COORDS[destName];
  if (!origin || !dest) return null;

  const miles = distanceMiles(origin.lat, origin.lng, dest.lat, dest.lng);

  // Look up dest.region from DESTINATIONS catalog so we can apply region-aware
  // pricing (domestic vs international). Falls back to null if not found,
  // which triggers the existing international tier logic — safe default.
  const destObj = (typeof DESTINATIONS !== "undefined")
    ? DESTINATIONS.find(d => d.name === destName)
    : null;
  const isDomestic = destObj?.subRegion === "North America";

  // ── Hawaii corridor override (precedence over both domestic & international) ──
  // Hawaii routes from US mainland are unusually competitive (Hawaiian,
  // Southwest, Alaska, Delta, United, American). Audit confirmed real ~$600
  // round-trip from CLT/East Coast — matches base $200 + $0.10/mi formula.
  const HAWAII_DESTS = new Set(["Honolulu", "Maui", "Kauai", "Kona"]);
  if (HAWAII_DESTS.has(destName)) {
    const price = 200 + miles * 0.10;
    return Math.round(price / 10) * 10;
  }

  let price;
  if (isDomestic) {
    // ── DOMESTIC US PRICING (Cas's audit, Jun 9 2026 + Jun 10 follow-up) ──
    // Real CLT round-trip economy at 30+ day advance fits: $150 base + $0.10/mi
    // with a $200 floor. This is the optimistic baseline for trip planning.
    // ── Jun 10 follow-up audit found this UNDER-priced last-minute trips by
    // 1.5-3x. Real-world domestic fares spike dramatically inside 14 days:
    // a 4-day-window CLT→ATL is ~$350, not $200. Added booking-window
    // multiplier + limited-service-airport premium below.
    price = Math.max(200, 150 + miles * 0.10);
    // Apply booking-window markup (no-op when no date given = optimistic baseline).
    // Pass miles so long-haul routes get the damping treatment.
    price = price * getDomesticBookingWindowMultiplier(dateStr, miles);
    // Limited-service-airport premium: CHS, SAV, etc. have constrained supply
    // and run ~1.5x competitive markets year-round.
    if (LIMITED_SERVICE_DOMESTIC.has(destName)) {
      price = price * 1.5;
    }
    // NO PEAK SEASONAL multiplier for domestic — Cas's audit found summer is
    // the CHEAPEST season for US leisure routes (Vegas, NYC, LA, Atlanta all
    // bottom out Jul-Aug due to business-travel suppression + supply spike).
    // Multiplying these up by 1.25× is the opposite of reality. Domestic
    // pricing stays flat across seasons in the model.
  } else {
    // ── INTERNATIONAL TIERED PRICING (unchanged from prior calibration) ──
    // <500mi: $240 + $0.20/mi (very short cross-border like CDG-LHR)
    // 500-1500mi: $180 + $0.32/mi
    // 1500-3500mi: $280 + $0.22/mi (continental crossings, short intl)
    // 3500-6000mi: $420 + $0.16/mi (long-haul)
    // 6000+: $580 + $0.13/mi (ultra-long)
    let base, rate;
    if (miles < 500)        { base = 240; rate = 0.20; }
    else if (miles < 1500)  { base = 180; rate = 0.32; }
    else if (miles < 3500)  { base = 280; rate = 0.22; }
    else if (miles < 6000)  { base = 420; rate = 0.16; }
    else                    { base = 580; rate = 0.13; }
    price = base + miles * rate;

    // Seasonal multiplier — APPLIED ONLY TO INTERNATIONAL.
    // Peak (Jun-Aug, Dec) +25%, off (Jan-Feb, Nov) -15%, else neutral.
    const month = dateStr ? new Date(dateStr).getMonth() : new Date().getMonth();
    const peakMonths = [5, 6, 7, 11];
    const offMonths = [0, 1, 10];
    let mult = 1.0;
    if (peakMonths.includes(month)) mult = 1.25;
    else if (offMonths.includes(month)) mult = 0.85;
    price = price * mult;
  }

  // Round to nearest $10
  return Math.round(price / 10) * 10;
}

// SafetyBadge — small advisory chip shown on destination cards and in the
// booking modal when a destination has a safetyNote. Tap reveals a brief
// explanation + a direct link to travel.state.gov for the current advisory.
// Designed to be transparent without scaring users away — uses sky-mid for
// Level 2 (most destinations) and sun-deep for Level 3 (notable extra care).
//
// Usage:
//   <SafetyBadge safetyNote={dest.safetyNote} variant="pill" />   // for cards
//   <SafetyBadge safetyNote={dest.safetyNote} variant="inline" /> // for booking modal
export function SafetyBadge({ safetyNote, variant = "pill", country = "" }) {
  const [expanded, setExpanded] = useState(false);
  if (!safetyNote) return null;
  const { level, note } = safetyNote;
  const color = level >= 3 ? "#C8741D" : "#4A8CC4";
  const bg = level >= 3 ? "rgba(200,116,29,0.08)" : "rgba(74,140,196,0.08)";
  const border = level >= 3 ? "rgba(200,116,29,0.4)" : "rgba(74,140,196,0.4)";
  const label = `Level ${level} advisory`;
  // Direct link to the country page on travel.state.gov if we have a country
  const advisoryUrl = country
    ? `https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories/${country.toLowerCase().replace(/[^a-z]/g, "-")}-travel-advisory.html`
    : "https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html";

  if (variant === "pill") {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setExpanded(v => !v); }}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "3px 8px", borderRadius: 12,
          background: bg, border: `1px solid ${border}`,
          color, fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
          fontFamily: "inherit", cursor: "pointer",
          textTransform: "uppercase",
        }}
        aria-label={`${label} — tap for details`}>
        <span aria-hidden="true">ⓘ</span>
        <span>{label}</span>
      </button>
    );
  }

  // inline variant — full row, shows note + link inline
  return (
    <div style={{
      background: bg, border: `1px solid ${border}`,
      borderRadius: 10, padding: "10px 12px", marginBottom: 12,
      fontSize: 11.5, lineHeight: 1.5, color: "#1B3A5C",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span aria-hidden="true" style={{ color }}>ⓘ</span>
        <span style={{ color, fontWeight: 800, letterSpacing: 0.3, fontSize: 10.5, textTransform: "uppercase" }}>{label}</span>
      </div>
      <div style={{ marginBottom: 4 }}>{note}</div>
      <a href={advisoryUrl} target="_blank" rel="noopener noreferrer"
         style={{ color, fontSize: 11, fontWeight: 700, textDecoration: "underline", textDecorationStyle: "dotted" }}>
        View current advisory on travel.state.gov →
      </a>
    </div>
  );
}

// ── Budget display helper — shows accurate or gated estimate ──────
// ── Trust Band — price match guarantee + payment logos ──────────
// Use `variant="compact"` for inline (above book buttons), `variant="full"` for footer
export function TrustBand({ variant = "full", darkMode = true, style = {} }) {
  const isCompact = variant === "compact";
  return (
    <div style={{ textAlign: "center", padding: isCompact ? "6px 0" : "10px 0", ...style }}>
      {!isCompact && (
        <p style={{ margin: "0 0 8px", fontSize: 11, color: darkMode ? "#4A6B8A" : "#7a9aaa", letterSpacing: 0.3, lineHeight: 1.3 }}>
          <span style={{ color: "#1B3A5C", fontWeight: 700 }}>✓ 100% Price Match Guarantee</span>
          <span style={{ margin: "0 8px", opacity: 0.3 }}>•</span>
          <span>Real trips planned for <strong style={{ color: darkMode ? "#1B3A5C" : "#0a3a3a" }}>real travelers</strong></span>
        </p>
      )}
      {isCompact && (
        <p style={{ margin: "0 0 6px", fontSize: 10, color: darkMode ? "#4A6B8A" : "#7a9aaa", letterSpacing: 0.3, lineHeight: 1 }}>
          <span style={{ color: "#1B3A5C", fontWeight: 700 }}>✓ 100% Price Match</span>
          <span style={{ margin: "0 6px", opacity: 0.3 }}>•</span>
          <span>🔒 Secured by Stripe</span>
        </p>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: isCompact ? 5 : 8, flexWrap: "wrap" }}>
        {/* Stripe */}
        <div style={{ display: "flex", alignItems: "center", padding: isCompact ? "2px 6px" : "3px 8px", borderRadius: 5, background: darkMode ? "rgba(99,91,255,0.12)" : "rgba(99,91,255,0.08)", border: `1px solid ${darkMode ? "rgba(99,91,255,0.25)" : "rgba(99,91,255,0.2)"}` }}>
          <svg aria-hidden="true" width={isCompact ? 28 : 34} height={isCompact ? 12 : 14} viewBox="0 0 60 24"><text x="0" y="18" fontSize="18" fontWeight="900" fill="#635bff" fontFamily="sans-serif">stripe</text></svg>
        </div>
        {/* Apple Pay */}
        <div style={{ display: "flex", alignItems: "center", gap: 3, padding: isCompact ? "2px 6px" : "3px 8px", borderRadius: 5, background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.05)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)"}` }}>
          <svg aria-hidden="true" width={isCompact ? 10 : 12} height={isCompact ? 12 : 14} viewBox="0 0 50 60" fill={darkMode ? "white" : "#1a1a1a"} opacity="0.85"><path d="M34.5 5.5c-2.1 2.5-5.5 4.4-8.8 4.1-.4-3.4 1.2-7 3.1-9.2C31-1.7 34.7-3.6 37.6-3.8c.4 3.5-1 6.9-3.1 9.3zM37.5 10.5c-4.9-.3-9 2.8-11.3 2.8-2.4 0-6-2.6-9.9-2.6C11 10.9 5.6 14.4 2.7 19.8c-5.9 10.2-1.5 25.3 4.2 33.6 2.8 4.1 6.1 8.6 10.5 8.4 4.2-.2 5.8-2.7 10.9-2.7 5.1 0 6.5 2.7 11 2.6 4.5-.1 7.4-4.1 10.2-8.2 3.2-4.7 4.5-9.2 4.6-9.4-.1-.1-8.8-3.4-8.9-13.5-.1-8.5 6.9-12.5 7.2-12.7-3.9-5.8-10-6.4-12.9-6.4z"/></svg>
          <span style={{ fontSize: isCompact ? 9 : 10, fontWeight: 700, color: darkMode ? "#e8e8e8" : "#1a1a1a" }}>Pay</span>
        </div>
        {/* Google Pay */}
        <div style={{ display: "flex", alignItems: "center", padding: isCompact ? "2px 6px" : "3px 8px", borderRadius: 5, background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.05)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)"}` }}>
          <span style={{ fontSize: isCompact ? 9 : 10, fontWeight: 700, color: darkMode ? "#e8e8e8" : "#202124", letterSpacing: 0.2 }}>G Pay</span>
        </div>
        {/* Visa */}
        <div style={{ padding: isCompact ? "2px 6px" : "3px 8px", borderRadius: 5, background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(26,31,113,0.06)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(26,31,113,0.15)"}` }}>
          <svg aria-hidden="true" width={isCompact ? 22 : 28} height={isCompact ? 10 : 12} viewBox="0 0 60 20"><text x="0" y="16" fontSize="18" fontWeight="900" fill={darkMode ? "#a0a8e0" : "#1a1f71"} fontFamily="sans-serif">VISA</text></svg>
        </div>
        {/* Mastercard */}
        <div style={{ padding: isCompact ? "2px 6px" : "3px 8px", borderRadius: 5, background: darkMode ? "rgba(27,58,92,0.072)" : "rgba(0,0,0,0.05)", border: `1px solid ${darkMode ? "rgba(27,58,92,0.144)" : "rgba(0,0,0,0.1)"}`, display: "flex", alignItems: "center" }}>
          <div aria-hidden="true" style={{ width: isCompact ? 10 : 12, height: isCompact ? 10 : 12, borderRadius: "50%", background: "#eb001b" }} />
          <div style={{ width: isCompact ? 10 : 12, height: isCompact ? 10 : 12, borderRadius: "50%", background: "#f79e1b", marginLeft: -4 }} />
        </div>
      </div>
    </div>
  );
}

export function Label({ children, color = "#4A8CC4" }) {
  const { darkMode } = useTheme();
  // Darken gold/amber labels for light mode readability
  const lightColor = color === "#4A8CC4" || color === "#4A8CC4" ? "#4A8CC4" : color;
  return <div style={{ fontSize: 11, letterSpacing: 3, color: darkMode ? color : lightColor, textTransform: "uppercase", marginBottom: 10 }}>{children}</div>;
}

// formatTravelers — single source of truth for traveler-count phrasing.
// `mode='full'` for prose ("Solo", "Couple", "3 travelers", "4 travelers").
// `mode='short'` for compact pickers ("Solo", "Couple", "3", "4").
// Bookings cap at 4 travelers (per Cas's product decision Jun 11 2026 —
// see FAQ entry for larger groups). Past-trip metadata can exceed 4, in
// which case n >= 5 renders as "5+ travelers" / "5+" — but that path
// shouldn't appear in booking surfaces.
// American spelling ("travelers", not "travellers") is canonical here.
export function formatTravelers(n, mode = "full") {
  const count = Number(n) || 1;
  if (mode === "short") {
    if (count === 1) return "Solo";
    if (count === 2) return "Couple";
    if (count >= 5) return "5+";
    return String(count);
  }
  // full mode
  if (count === 1) return "Solo";
  if (count === 2) return "Couple";
  if (count >= 5) return "5+ travelers";
  return `${count} travelers`;
}

export const cardStyle = {
  background: "rgba(13,21,33,0.95)",
  border: "1px solid rgba(74,140,196,0.18)",
  borderRadius: 20,
  padding: "28px 24px",
  WebkitBackdropFilter: "blur(10px)", backdropFilter: "blur(10px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(27,58,92,0.060)",
};

// Light-mode aware card helper used in sub-components
export function lightCard(darkMode) {
  return darkMode ? cardStyle : {
    background: "#ffffff",
    border: "1px solid rgba(27,58,92,0.2)",
    borderRadius: 20,
    padding: "28px 24px",
    WebkitBackdropFilter: "none", backdropFilter: "none",
    boxShadow: "0 2px 24px rgba(27,58,92,0.1)",
  };
}

// Default export object — CRA/Webpack defensive workaround (safeStorage.js precedent).
const shared = {
  BOOKING_OPEN_DATE,
  getMinTravelDate,
  addDaysISO,
  daysBetweenISO,
  applyDepartChange,
  applyReturnChange,
  isPreLaunch,
  ThemeContext,
  useTheme,
  DESTINATIONS,
  DEST_COORDS,
  LIMITED_SERVICE_DOMESTIC,
  MAJOR_AIRPORTS,
  distanceMiles,
  getDomesticBookingWindowMultiplier,
  estimateFlightPrice,
  SafetyBadge,
  TrustBand,
  Label,
  formatTravelers,
  cardStyle,
  lightCard,
};
export default shared;
